import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotesStore } from '../store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, Plus, Trash2, FileText } from 'lucide-react';

const DEFAULT_TEMPLATES = [
  { name: 'Meeting Notes', content: '# Meeting Notes\n\n## Agenda\n- \n\n## Attendees\n- \n\n## Discussion\n\n## Action Items\n- [ ] \n\n## Next Steps\n' },
  { name: 'Daily Journal', content: '# {{date}}\n\n## What I did today\n- \n\n## What I learned\n- \n\n## Tomorrow\n- ' },
  { name: 'Project README', content: '# Project Name\n\n## Description\n\n## Features\n- \n\n## Tech Stack\n\n## Getting Started\n\n## License\n' },
  { name: 'Book Notes', content: '# {{title}}\n\n**Author:** \n**Rating:** ⭐⭐⭐⭐⭐\n\n## Key Takeaways\n- \n\n## Notable Quotes\n> \n\n## Summary\n' },
  { name: 'Lecture Notes', content: '# {{subject}} - Lecture {{number}}\n\n## Key Concepts\n- \n\n## Notes\n\n## Questions\n- \n\n## Summary\n' },
];

export default function TemplatesView() {
  const navigate = useNavigate();
  const { templates, fetchTemplates, createTemplate, deleteTemplate, createNote } = useNotesStore();
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');

  useEffect(() => { fetchTemplates(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    await createTemplate({ name, description, content });
    setShowNew(false); setName(''); setDescription(''); setContent('');
  };

  const useTemplate = async (template) => {
    const note = await createNote({ title: template.name, content: template.content, folder: '/Templates' });
    navigate(`/note/${note.id}`);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><BookOpen size={20} /> Templates</h1>
          <p className="text-sm text-muted-foreground">Reusable note templates</p>
        </div>
        <Button onClick={() => setShowNew(!showNew)}><Plus size={16} className="mr-2" /> New Template</Button>
      </div>

      {showNew && (
        <Card>
          <CardHeader><CardTitle>Create Template</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-3">
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Template name" required />
              <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)" />
              <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Template content..." className="w-full h-40 p-3 bg-muted border rounded-lg text-sm font-mono focus:outline-none focus:ring-1 focus:ring-ring" />
              <div className="flex gap-2">
                <Button type="submit" size="sm">Create</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowNew(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div>
        <h2 className="text-sm font-medium text-muted-foreground mb-3">Default Templates</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {DEFAULT_TEMPLATES.map(t => (
            <Card key={t.name} className="hover:shadow-md transition-all cursor-pointer" onClick={() => useTemplate(t)}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><FileText size={14} /> {t.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground line-clamp-2">{t.content.slice(0, 100)}...</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {templates.length > 0 && (
        <div>
          <h2 className="text-sm font-medium text-muted-foreground mb-3">Your Templates</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates.map(t => (
              <Card key={t.id}>
                <CardContent className="flex items-center justify-between p-4">
                  <div className="cursor-pointer flex-1" onClick={() => useTemplate(t)}>
                    <h3 className="font-medium">{t.name}</h3>
                    <p className="text-xs text-muted-foreground">{t.description || 'No description'}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => deleteTemplate(t.id)}>
                    <Trash2 size={14} />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
